import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Icon } from "@iconify/react";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.mil-168.com"
).replace(/\/+$/, "");

const PAGE_SIZE = 9;

function maskUsername(username) {
  if (!username) return "****";
  if (username.length <= 4) return "****";
  return username.slice(0, username.length - 4) + "****";
}

function StarDisplay({ value }) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          style={{
            fontSize: 17,
            color: n <= value ? "#facc15" : "rgba(255,255,255,0.18)",
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ReviewsModal({ isOpen, onClose }) {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const bodyScrollPos = useRef(0);

  const fetchReviews = useCallback(async (page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/reviews/public`, {
        params: { page, limit: PAGE_SIZE },
      });
      setReviews(res.data?.data || []);
      setPagination({
        page: res.data?.pagination?.page || 1,
        totalPages: res.data?.pagination?.totalPages || 1,
        total: res.data?.pagination?.total || 0,
      });
    } catch (_) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      bodyScrollPos.current = window.scrollY;
      document.body.style.overflow = "hidden";
      fetchReviews(1);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, fetchReviews]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const goToPage = (p) => {
    fetchReviews(p);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(6px)",
      }}
      onMouseDown={onClose}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(700px, 100%)",
          maxHeight: "90vh",
          background:
            "linear-gradient(170deg, color-mix(in srgb, var(--primaryColor) 20%, #0d0d14) 0%, #0d0d14 100%)",
          borderTop:
            "1px solid color-mix(in srgb, var(--primaryColor) 50%, transparent)",
          borderLeft:
            "1px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
          borderRight:
            "1px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
          borderRadius: "22px 22px 0 0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 -16px 48px rgba(0,0,0,0.7)",
          animation: "reviewsSlideUp 0.28s cubic-bezier(0.34,1.28,0.64,1)",
        }}
      >
        <style>{`
          @keyframes reviewsSlideUp {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px 14px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Icon icon="fluent-color:star-emphasis-24" width={26} />
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>
                รีวิวจากลูกค้า
              </div>
              {pagination.total > 0 && (
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
                  {pagination.total} รีวิวทั้งหมด
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 10,
              color: "#fff",
              width: 34,
              height: 34,
              cursor: "pointer",
              fontSize: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ overflowY: "auto", flex: 1, padding: "16px 16px 0" }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  border: "3px solid rgba(255,255,255,0.12)",
                  borderTopColor: "var(--primaryColor, #f97316)",
                  borderRadius: "50%",
                  animation: "rev-spin 0.7s linear infinite",
                }}
              />
              <style>{`@keyframes rev-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : reviews.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px 0",
                color: "rgba(255,255,255,0.35)",
                fontSize: 14,
              }}
            >
              <Icon
                icon="solar:star-shine-bold-duotone"
                width={44}
                style={{
                  opacity: 0.4,
                  display: "block",
                  margin: "0 auto 12px",
                }}
              />
              ยังไม่มีรีวิว
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 10,
              }}
            >
              {reviews.map((r) => (
                <div
                  key={r.id}
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 14,
                    padding: "13px 14px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 7,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.85)",
                      }}
                    >
                      {maskUsername(r.username)}
                    </span>
                    <StarDisplay value={r.rating} />
                  </div>
                  {r.message && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: "rgba(255,255,255,0.68)",
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {r.message}
                    </p>
                  )}
                  <span
                    style={{ fontSize: 11, color: "rgba(255,255,255,0.28)" }}
                  >
                    {formatDate(r.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              padding: "14px 16px",
              borderTop: "1px solid rgba(255,255,255,0.07)",
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => goToPage(pagination.page - 1)}
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 9,
                color: "#fff",
                padding: "7px 18px",
                cursor: pagination.page <= 1 ? "not-allowed" : "pointer",
                opacity: pagination.page <= 1 ? 0.4 : 1,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              ← ก่อนหน้า
            </button>

            {/* Page numbers */}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter((p) => Math.abs(p - pagination.page) <= 2)
                .map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goToPage(p)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      border:
                        p === pagination.page
                          ? "1px solid var(--primaryColor, #f97316)"
                          : "1px solid rgba(255,255,255,0.14)",
                      background:
                        p === pagination.page
                          ? "var(--primaryColor, #f97316)"
                          : "rgba(255,255,255,0.07)",
                      color: "#fff",
                      fontWeight: p === pagination.page ? 700 : 400,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    {p}
                  </button>
                ))}
            </div>

            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => goToPage(pagination.page + 1)}
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 9,
                color: "#fff",
                padding: "7px 18px",
                cursor:
                  pagination.page >= pagination.totalPages
                    ? "not-allowed"
                    : "pointer",
                opacity: pagination.page >= pagination.totalPages ? 0.4 : 1,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              ถัดไป →
            </button>
          </div>
        )}

        {/* safe area */}
        <div style={{ height: 8, flexShrink: 0 }} />
      </div>
    </div>
  );
}
